import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useIsMobileMd } from '../../hooks/useMatchMedia';
import './ySheet.css';

/**
 * App form slide-over. Desktop: right-edge drawer. Phone: bottom sheet.
 * Token-only chrome. Do not use for the map Layers panel.
 */
export function YSheet({
  show,
  onHide,
  title,
  kicker,
  children,
  footer,
  footerStart,
  error,
  testId = 'y-sheet',
  className = '',
  bodyClassName = '',
}) {
  const isPhone = useIsMobileMd();
  const titleId = useId();
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!show) return undefined;
    previousFocusRef.current = document.activeElement;
    document.body.classList.add('y-sheet-open');
    const frame = window.requestAnimationFrame(() => {
      panelRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.classList.remove('y-sheet-open');
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onHide();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [show, onHide]);

  if (!show || typeof document === 'undefined') return null;

  const placement = isPhone ? 'bottom' : 'end';

  return createPortal(
    <div
      className={['y-sheet-root', `y-sheet-root--${placement}`, className].filter(Boolean).join(' ')}
      data-testid={testId}
      role="presentation"
    >
      <button
        type="button"
        className="y-sheet-scrim"
        data-testid={`${testId}-scrim`}
        aria-label="Dismiss"
        onClick={onHide}
      />
      <div
        ref={panelRef}
        className="y-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="y-sheet__header">
          <div className="y-sheet__titles">
            {kicker ? <span className="y-sheet__kicker">{kicker}</span> : null}
            <h2 id={titleId} className="y-sheet__title">
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="y-sheet__close"
            aria-label="Close"
            onClick={onHide}
          >
            ×
          </button>
        </header>
        <div className={['y-sheet__body', bodyClassName].filter(Boolean).join(' ')}>
          {error ? (
            <p className="y-sheet__error" role="alert">
              {error}
            </p>
          ) : null}
          {children}
        </div>
        {footerStart || footer ? (
          <footer className="y-sheet__footer">
            {footerStart ? <div className="y-sheet__footer-start">{footerStart}</div> : null}
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

YSheet.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  title: PropTypes.node,
  kicker: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  footerStart: PropTypes.node,
  error: PropTypes.string,
  testId: PropTypes.string,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
};
